import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Box, Button, IconButton, Typography } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import GoogleIcon from '@mui/icons-material/Google'
import WindowIcon from '@mui/icons-material/Window'
import { TextFieldWithLabel } from 'Components/TextField'
import * as AppRoutes from 'Pages/Routes'
import mieccLogo from 'Assets/Logos/MiECC.png'
import loginChildrenPhoto from 'Assets/Images/LoginChildren.jpg'

const WELCOME_PARAGRAPH = 'MiECC is Michigan’s statewide system for early childhood, bringing together programs and providers across the state to help families find and access the services their children need.'
const WelcomeNotLoggedInAdditionalText = 'Sign in to get started, or create an account to begin.'
const SHORT_TAGLINE = 'MiECC is Michigan’s statewide system for early childhood programs'

const ssoButtonSx = {
  textTransform: 'none',
  justifyContent: 'center',
  color: 'text.primary',
  bgcolor: '#F5F5F5',
  borderColor: '#E7E7E7',
  borderRadius: '8px',
  '&:hover': { bgcolor: '#EBEBEB', borderColor: '#C7C7C7' }
} as const

export function WelcomeCopy ({ headingVariant, loggedIn }: { headingVariant: 'h4' | 'h5', loggedIn: boolean }): JSX.Element {
  return (
    <>
      <Typography variant={headingVariant} component="h2" sx={{ mb: 1.5 }}>
        Welcome to MiEarly Childhood Connect!
      </Typography>
      <Typography sx={{ color: 'text.secondary' }}>
        {WELCOME_PARAGRAPH}{!loggedIn && ` ${WelcomeNotLoggedInAdditionalText}`}
      </Typography>
    </>
  )
}

// Same width as its panel. This image's own aspect ratio (~1.4:1) is narrower than most frames it's
// asked to cover, so plain object-fit:cover already crops most of the blank wall off the top on its
// own; anchoring to the bottom keeps the kids fully visible. The fade mask lives on the outer
// (visible) frame, not the image itself.
//
// fillRemaining: true makes the frame stretch to consume whatever vertical space is left in its
// flex column (used on desktop, inside the branding column, so the photo reaches the footer on
// any screen height rather than being capped to a fixed ratio). false uses a fixed 16:9 band
// instead (used on mobile, which has no "remaining column space" to fill).
export function WelcomePhoto ({ fillRemaining = false }: { fillRemaining?: boolean }): JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(fillRemaining ? { flex: 1, minHeight: 0 } : { aspectRatio: '16 / 9' }),
        overflow: 'hidden',
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 50%, black 100%)'
      }}
    >
      <Box
        component="img"
        src={loginChildrenPhoto}
        alt=""
        sx={{
          // Absolutely positioned so it never contributes to the frame's own auto-height calc -
          // with fillRemaining, the frame's height comes only from flex-grow (a normal in-flow
          // height:100% img falls back to its intrinsic aspect ratio against an auto-height
          // ancestor and pushes the column taller than intended).
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center bottom'
        }}
      />
    </Box>
  )
}

export function LoginPage (): JSX.Element {
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState<string>(() => searchParams.get('email') ?? '')
  const [password, setPassword] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const errorParam = searchParams.get('error')
  const hasError = errorParam === '1'
  const isPendingConfirmation = errorParam === 'pending'
  // returnUrl must be an absolute client-origin URL - the whole login round-trip passes through
  // the server's own origin (and, on success, MiCredentials' origin too), so a bare relative path
  // would resolve against whichever of those the browser lands on, not this SPA.
  const returnUrl = searchParams.get('returnUrl') ?? (process.env.REACT_APP_CLIENT_URL ?? '/')
  const serverUrl = process.env.REACT_APP_SERVER_URL ?? ''

  // The Localize.com translation widget injects itself into #localize-widget outside the React
  // tree, asynchronously, and re-asserts its own visibility (e.g. on viewport resize) - keep
  // re-hiding it for as long as this page is mounted to stay chrome-free, matching the Figma
  // mockup, and restore it on unmount for every other page.
  useEffect(() => {
    const hide = (): void => {
      const widget = document.getElementById('localize-widget')
      if (widget != null && widget.style.display !== 'none') widget.style.display = 'none'
    }

    hide()
    const observer = new MutationObserver(hide)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] })

    return () => {
      observer.disconnect()
      const widget = document.getElementById('localize-widget')
      if (widget != null) widget.style.display = ''
    }
  }, [])

  const loginWithGoogle = (): void => {
    window.location.href = `${serverUrl}/Account/GoogleLogin?returnUrl=${encodeURIComponent(returnUrl)}`
  }

  const loginWithMicrosoft = (): void => {
    window.location.href = `${serverUrl}/Account/MicrosoftLogin?returnUrl=${encodeURIComponent(returnUrl)}`
  }

  return (
    // The shared Layout's <Container> reserves an extra 40px of bottom padding (for the fixed
    // Footer) on every page, so a plain minHeight:100vh here would always overflow by 40px - this
    // gives the branding column's flex:1 photo a real height to grow into, right down to the footer.
    <Box sx={{ bgcolor: '#fff', minHeight: 'calc(100vh - 40px)' }}>
      {/* Compact mobile-only header: logo + short tagline (desktop shows the full branding panel instead) */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', pt: 5, px: 4 }}>
        <Box component="img" src={mieccLogo} alt="MiEarly Childhood Connect" sx={{ width: '175px', mb: 2 }} />
        <Typography sx={{ color: 'text.secondary' }}>{SHORT_TAGLINE}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Desktop-only branding panel: text block on top, photo filling the rest of the column down
            to the footer. minHeight here (not just on the page root) is what gives this flex row a
            real height for the row to stretch to - flex:1 on the photo needs something concrete to
            fill. */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            flex: '0 0 50%',
            minHeight: 'calc(100vh - 40px)'
          }}
        >
          <Box sx={{ pt: 8, px: 6, pb: 4, textAlign: 'center' }}>
            <Box component="img" src={mieccLogo} alt="MiEarly Childhood Connect" sx={{ width: '100%', maxWidth: '320px', mb: 3, mx: 'auto' }} />
            <WelcomeCopy headingVariant="h5" loggedIn={false} />
          </Box>
          <WelcomePhoto fillRemaining />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 4, py: 5 }}>
          <Box sx={{ width: '100%', maxWidth: '680px' }}>
            <Typography variant="h4" component="h1" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              Login To Your Account
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
              <Button
                variant="outlined"
                onClick={loginWithGoogle}
                startIcon={<GoogleIcon />}
                sx={ssoButtonSx}
                data-testid="login-google"
              >
                Sign in with Google
              </Button>
              <Button
                variant="outlined"
                onClick={loginWithMicrosoft}
                startIcon={<WindowIcon />}
                sx={ssoButtonSx}
                data-testid="login-microsoft"
              >
                Sign in with Microsoft
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>or</Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
            </Box>

            <Typography sx={{ textAlign: 'center', mb: 2, fontWeight: 600 }}>
              Enter Your MiCredentials
            </Typography>

            <form method="post" action={`${serverUrl}/Account/LoginWithUser`}>
              <input type="hidden" name="returnUrl" value={returnUrl} />

              <TextFieldWithLabel
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={event => { setEmail(event.target.value) }}
                required
                maxWidth="100%"
                placeHolder="Enter your email"
                dataTestId="login-email"
              />

              <TextFieldWithLabel
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={event => { setPassword(event.target.value) }}
                required
                maxWidth="100%"
                placeHolder="Enter your password"
                dataTestId="login-password"
                endAdornment={
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => { setShowPassword(!showPassword) }}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                }
              />

              {hasError && (
                <Typography sx={{ color: 'error.main', mt: 1 }} data-testid="login-error">
                  Invalid email or password.
                </Typography>
              )}

              {isPendingConfirmation && (
                <Typography sx={{ color: 'error.main', mt: 1 }} data-testid="login-error-pending">
                  Please confirm your account before signing in - check your email for a confirmation link.
                </Typography>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ textTransform: 'none', mt: 3 }}
                data-testid="login-submit"
              >
                Login
              </Button>
            </form>

            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Link to={AppRoutes.FORGOT_PASSWORD}>Forgot Password?</Link>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile-only welcome panel, repeated below the form (matches the "Login - Mobile" mockup).
          Text keeps the page's side padding; the photo bleeds edge-to-edge with none. */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <Box sx={{ px: 4, py: 5, textAlign: 'center' }}>
          <WelcomeCopy headingVariant="h5" loggedIn={false}/>
        </Box>
        <WelcomePhoto />
      </Box>
    </Box>
  )
}
